import { RegItemMinimal } from 'src/types/regItem'
import { Offer } from 'src/types/offer'

import {
  REG_ITEM_TYPE_CROWDFUND,
  REG_ITEM_TYPE_EXTERNAL_REGISTRY,
  REG_ITEM_TYPE_FAVOR,
  REG_ITEM_TYPE_GIFT_CARD,
  REG_ITEM_TYPE_GROUP_GIFT,
  REG_ITEM_TYPE_PRODUCT,
} from 'src/constants'
import { getPriceTagPropsFromPriceDetails } from 'src/utils/productHelpers'
import getRegItemType from '../../../../utils/getRegItemType/getRegItemType'
import {
  CashFundDetails,
  ExternalRegistryDetails,
  GiftCardDetails,
  GroupGiftDetails,
  HelpAndFavorDetails,
  ProductDetails,
  ReservedDetails,
} from './variants'
import { ReservationWithVisitor } from '../../../RegistryCategories/RegistryCategories.types'

export const getRegItemDetailsComponent = (
  regItem: RegItemMinimal,
  reservation?: ReservationWithVisitor
) => {
  if (reservation) {
    return ReservedDetails
  }

  const regItemType = getRegItemType(regItem)

  switch (regItemType) {
    case REG_ITEM_TYPE_CROWDFUND:
      return CashFundDetails
    case REG_ITEM_TYPE_GIFT_CARD:
      return GiftCardDetails
    case REG_ITEM_TYPE_EXTERNAL_REGISTRY:
      return ExternalRegistryDetails
    case REG_ITEM_TYPE_FAVOR:
      return HelpAndFavorDetails
    case REG_ITEM_TYPE_GROUP_GIFT:
      return GroupGiftDetails
    case REG_ITEM_TYPE_PRODUCT:
      return ProductDetails
    default:
      return ProductDetails
  }
}

const getOfferBoundaries = (
  offers: Offer[]
): {
  lowest: Offer
  highest: Offer
} => {
  let lowest: Offer = offers[0]
  let highest: Offer = offers[0]

  offers.forEach((offer) => {
    const priceAsNumber = Number(offer.price)
    const lowestPriceAsNumber = Number(lowest.price)
    const highestPriceAsNumber = Number(highest.price)

    if (priceAsNumber < lowestPriceAsNumber) {
      lowest = offer
    }

    if (priceAsNumber > highestPriceAsNumber) {
      highest = offer
    }
  })

  return {
    lowest,
    highest,
  }
}

export const getPricingData = (regItem: RegItemMinimal) => {
  if (!regItem.offers || regItem.offers.length < 1) {
    // If we don't have and offers we fall back to the price on the regItem
    return {
      price: Number(regItem.price || 0),
    }
  }

  // Get the lowest and highest offers
  const { lowest, highest } = getOfferBoundaries(regItem.offers)

  // use getPriceTagPropsFromPriceDetails to handle sale data and price range data logic
  // For most external offers this isn't needed but it handles the babylist offers and is
  // a good fallback for any edge cases.
  const {
    currentPrice: lowestCurrentPrice,
    minPrice: lowestPriceRangeMin,
    msrp: lowestMsrp,
  } = getPriceTagPropsFromPriceDetails(lowest.priceDetails)
  const {
    currentPrice: highestCurrentPrice,
    maxPrice: highestPriceRangeMax,
    msrp: highestMsrp,
  } = getPriceTagPropsFromPriceDetails(highest.priceDetails)

  // Priority order for values:
  // 1. Price range (If a price range is present we want to use that first)
  // 2. Current price (uses sale prices if present otherwise falls back to list price)
  // 3. Offer base price value (not a price range)
  const min = lowestPriceRangeMin || lowestCurrentPrice || lowest.price
  const max = highestPriceRangeMax || highestCurrentPrice || highest.price

  // Use the highest msrp if it exists, otherwise use the lowest msrp if it exists.
  const msrp = highestMsrp || lowestMsrp

  return {
    priceRange: {
      min: Number(min),
      max: Number(max),
    },
    strikePrice: msrp ? Number(msrp) : undefined,
    price: Number(min),
  }
}

export const isRegItemOnSale = (regItem: RegItemMinimal) =>
  regItem.priceDetails?.saleAttributes?.saleType === 'active_sale'

export const getSaleBadgeLabel = (regItem: RegItemMinimal) => {
  const { salePercentOff } = regItem.priceDetails?.saleAttributes || {}

  if (!salePercentOff?.percentInt || salePercentOff.percentInt < 10) {
    return 'Sale'
  }

  return `${salePercentOff.percentInt}% off`
}

export const isRegItemOutOfStock = (regItem: RegItemMinimal) =>
  regItem.offers?.length === 1 &&
  regItem.offers[0].storeDisplayName === 'Babylist' &&
  !regItem.isAvailableOnBabylist
